<template>
  <RfDialog
    ref="dialog"
    :title="`${provider.name} Details`"
    width="700"
    closeOnClickOutside
    confirmText="Save changes"
    cancelText="Close"
    @confirm="emit('save')"
  >
    <div v-if="provider.columns" class="connector-edit-wrapper salesforce-connector">
      <RfSwitch
        v-for="field in leadSwitches"
        :key="field.key"
        :modelValue="!!fieldValue(field)"
        :disabled="fieldDisabled"
        title="Active"
        class="mb-5"
        @update:modelValue="value => emit('updateField', field, value)"
      />
      <div class="connectors-col">
        <div class="settings-salesforce-col1 flex flex-col gap-5">
          <RfInput
            v-for="field in columnFields(1)"
            :key="field.key"
            :modelValue="fieldValue(field) ?? ''"
            :title="field.title"
            :placeholder="field.placeholder ?? ''"
            variant="outlined"
            size="small"
            :disabled="fieldDisabled"
            @update:modelValue="value => emit('updateField', field, value)"
          />
        </div>
        <div class="settings-salesforce-col2 flex flex-col gap-5">
          <RfInput
            v-for="field in columnFields(2)"
            :key="field.key"
            :modelValue="fieldValue(field) ?? ''"
            :title="field.title"
            :placeholder="field.placeholder ?? ''"
            variant="outlined"
            size="small"
            :disabled="fieldDisabled"
            @update:modelValue="value => emit('updateField', field, value)"
          />
        </div>
      </div>
      <RfConnectorActionsTable v-if="actionsField" class="mt-5" :actions="resolvedActions" />
    </div>
    <div v-else :class="{ 'connector-edit-wrapper': !provider.bare }">
      <div class="connector-edit-div flex flex-col gap-5">
        <div v-if="flagDisabled" class="bg-goal rounded px-4 py-3">
          <v-icon size="16">md:info</v-icon>
          <span class="ml-1 align-middle">
            Contact Customer Support to enable this integration for your app.
          </span>
        </div>

        <template v-for="(field, index) in fields" :key="index">
          <RfSwitch
            v-if="field.type === 'switch'"
            :modelValue="!!fieldValue(field)"
            :disabled="fieldDisabled"
            title="Active"
            @update:modelValue="value => emit('updateField', field, value)"
          />
          <RfCheckbox
            v-else-if="field.type === 'checkbox'"
            :modelValue="!!fieldValue(field)"
            :disabled="fieldDisabled"
            size="small"
            @update:modelValue="value => emit('updateField', field, value)"
          >
            {{ field.label }}
          </RfCheckbox>
          <RfSelect
            v-else-if="field.type === 'select'"
            :modelValue="fieldValue(field) ?? ''"
            :items="field.items"
            :title="field.title"
            :placeholder="field.placeholder"
            :hintMessage="field.hint"
            variant="outlined"
            size="small"
            sameWidth
            :disabled="fieldDisabled"
            @update:modelValue="value => emit('updateField', field, value)"
          />
          <RfTextarea
            v-else-if="field.type === 'text' && field.textarea"
            :modelValue="fieldValue(field) ?? ''"
            :title="field.title"
            :placeholder="field.placeholder ?? ''"
            variant="outlined"
            :disabled="fieldDisabled"
            @update:modelValue="value => emit('updateField', field, value)"
          />
          <RfInput
            v-else-if="field.type === 'text'"
            :modelValue="fieldValue(field) ?? ''"
            :title="field.title"
            :placeholder="field.placeholder ?? ''"
            :hintMessage="field.hint"
            variant="outlined"
            size="small"
            :disabled="fieldDisabled || field.readonly"
            @update:modelValue="value => emit('updateField', field, value)"
          />
          <h3 v-else-if="field.type === 'heading'">{{ field.text }}</h3>
          <div v-else-if="field.type === 'note'" class="settings-actions-list-items">
            <p>
              <v-icon size="16">md:info</v-icon>
              {{ field.text }}
            </p>
            <template v-if="field.events">
              <h5>Available Events</h5>
              <ul class="list-inside list-disc">
                <li v-for="event in ANALYTICS_EVENTS" :key="event">{{ event }}</li>
              </ul>
            </template>
          </div>
          <ConnectorApplePushRow
            v-else-if="field.component === 'applePushRow'"
            :modelValue="!!fieldValue(field)"
            :disabled="fieldDisabled"
            :formDirty="formDirty"
            @update:modelValue="value => emit('updateField', field, value)"
          />
          <ConnectorStripeConnect
            v-else-if="field.component === 'stripeConnect'"
            :form="form"
            :providerKey="provider.key"
            :url="stripeConnectUrl"
          />
          <RfAppleProductIds
            v-else-if="field.component === 'appleProductIds'"
            :app="app"
            :disabled="fieldDisabled"
          />
          <RfTestPushSender
            v-else-if="field.component === 'testPushSender'"
            :channel="field.channel"
            :disabled="fieldDisabled || (formDirty && 'Save your changes first')"
          />
        </template>

        <RfConnectorActionsTable v-if="actionsField?.inside" :actions="resolvedActions" />
      </div>
      <RfConnectorActionsTable
        v-if="actionsField && !actionsField.inside"
        class="mt-5"
        :actions="resolvedActions"
      />
    </div>
  </RfDialog>
</template>

<script setup>
import { computed, ref } from "vue";
import { ANALYTICS_EVENTS } from "@/utils/connectors/providerSchemas";
import RfDialog from "@/components/modals/RfDialog.vue";
import RfCheckbox from "@/components/inputs/RfCheckbox.vue";
import RfInput from "@/components/inputs/RfInput.vue";
import RfSelect from "@/components/inputs/RfSelect.vue";
import RfSwitch from "@/components/inputs/RfSwitch.vue";
import RfTextarea from "@/components/inputs/RfTextarea.vue";
import RfAppleProductIds from "@/components/RfSettings/RfAppleProductIds.vue";
import RfConnectorActionsTable from "@/components/RfSettings/RfConnectorActionsTable.vue";
import RfTestPushSender from "@/components/RfPromotions/RfTestPushSender.vue";
import ConnectorApplePushRow from "@/components/RfSettings/connectors/ConnectorApplePushRow.vue";
import ConnectorStripeConnect from "@/components/RfSettings/connectors/ConnectorStripeConnect.vue";

const props = defineProps({
  /** Schema entry from providerSchemas.js (PROVIDER_SCHEMAS item). */
  provider: { type: Object, required: true },
  /** Draft copy of `app.providers` the fields bind to (merged on save by the parent). */
  form: { type: Object, default: () => ({}) },
  app: { type: Object, default: () => ({}) },
  disabled: { type: Boolean, default: false },
  flagDisabled: { type: Boolean, default: false },
  formDirty: { type: Boolean, default: false },
  stripeConnectUrl: { type: String, default: "" },
  actionDescriptions: { type: Function, default: () => () => [] },
});

const emit = defineEmits(["save", "updateField"]);

const dialog = ref(null);

const open = () => dialog.value?.open();

defineExpose({ open });

const fields = computed(() => props.provider.fields || []);
const fieldDisabled = computed(() => props.disabled || props.flagDisabled);
const leadSwitches = computed(() =>
  fields.value.filter(field => !field.col && field.type === "switch"),
);
const columnFields = column => fields.value.filter(field => field.col === column);
const actionsField = computed(() => fields.value.find(field => field.type === "actions"));
const resolvedActions = computed(() => {
  const field = actionsField.value;
  if (!field) return [];
  return typeof field.actions === "string"
    ? props.actionDescriptions(field.actions)
    : field.actions;
});

/**
 * Reads a field's current value from its binding target.
 *
 * @param {Object} field - FieldDescriptor from the provider schema.
 * @returns {*} Current value; undefined when the key was never set.
 */
function fieldValue(field) {
  if (field.bind === "flags") return props.app.flags?.[field.key];
  if (field.bind === "appProviders") {
    return props.app.providers?.[props.provider.key]?.[field.key];
  }
  return props.form?.[field.provider || props.provider.key]?.[field.key];
}
</script>
